"use client";
import { useEffect, useState, useMemo } from "react";
import useUserEnrollments from "@/components/CourseContent/CourseContext/hooks/useUserEnrollments";
import { useCartContext } from "@/contexts/CartProvider";
import canCourseBePurchased from "@/lib/course/utils/canCourseBePurchased";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import newrelic from "@/lib/monitoring/newrelic";
import type { Enrollment } from "@/lib/partner-api/getEnrollments";
import { CartData } from "@/utils/cartUtils";
import { STORAGE_KEY, type ProductView } from "./useProductViewStorage";
import type { AbandonedCartSuggestionCardProps } from "@/components/AbandonedCartSuggestions/AbandonedCartSuggestionCard";

interface AbandonedCartIntent {
  suggestedProducts: ProductView[];
  isLoading: boolean;
  error: Error | null;
  cardProps?: AbandonedCartSuggestionCardProps[];
}

const ALLOWED_PRODUCT_TYPES = ["course", "program"];

function productIsInCart(product: ProductView, cartData: CartData) {
  return (
    cartData?.items.some((item) =>
      [item.sku, item.bundleId].includes(product.productId),
    ) || false
  );
}

function filterViewedProducts(
  viewedProducts: ProductView[],
  userEnrollments: Enrollment[] | null,
  cartData: CartData,
  maxProducts: number,
) {
  let count = 0;
  return viewedProducts
    .sort((a, b) => b.viewedAt - a.viewedAt)
    .filter((product) => {
      if (count < maxProducts) {
        if (productIsInCart(product, cartData)) {
          // already in cart
          return false;
        }

        if (product.productType === "course") {
          // return false if:
          // - user is already enrolled in non-audit mode
          // - enrollment or course run have ended
          // - enrollment hasn't started
          const courseCanBePurchased = canCourseBePurchased(
            {
              key: product.productId,
              enrollmentEnd: product.metadata?.enrollmentEnd
                ? new Date(product.metadata.enrollmentEnd)
                : null,
              end: product.metadata?.courseRunEnd
                ? new Date(product.metadata.courseRunEnd)
                : null,
              enrollmentStart: product.metadata?.enrollmentStart
                ? new Date(product.metadata.enrollmentStart)
                : null,
            },
            userEnrollments,
          );
          if (courseCanBePurchased) {
            count++;
            return true;
          }
          return false;
        }

        // always true for programs
        count++;
        return true;
      }

      // reached max products
      return false;
    });
}

export default function useAbandonedCartIntent(
  maxProducts = 1,
): AbandonedCartIntent {
  const { flagValue } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_PRODUCT_VIEW_TRACKING",
    "legacy",
  );
  const isFeatureEnabled = flagValue === "test";

  const { userEnrollments } = useUserEnrollments();
  const {
    state: { cartData },
  } = useCartContext();

  const [suggestedProducts, setSuggestedProducts] = useState<ProductView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isFeatureEnabled) {
      setIsLoading(false);
      return;
    }
    try {
      const viewedProductsRaw = localStorage.getItem(STORAGE_KEY);
      if (viewedProductsRaw) {
        const viewedProducts = JSON.parse(viewedProductsRaw);

        if (Array.isArray(viewedProducts)) {
          const filteredViewedProducts = viewedProducts.filter(
            (product: ProductView) =>
              ALLOWED_PRODUCT_TYPES.includes(product.productType),
          );

          setSuggestedProducts(
            filterViewedProducts(
              filteredViewedProducts,
              userEnrollments,
              cartData,
              maxProducts,
            ),
          );
        }
      }
    } catch (e) {
      newrelic.noticeError(e as Error);
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, [cartData, isFeatureEnabled, maxProducts, userEnrollments]);

  // Transform suggestions into card props
  const cardProps = useMemo(():
    | AbandonedCartSuggestionCardProps[]
    | undefined => {
    if (suggestedProducts.length === 0) {
      return undefined;
    }

    return suggestedProducts.map((productView) => {
      // Get subject/category from metadata
      const subject = productView.metadata?.category || "";

      // Get product name
      const productName = productView.productName || "Product";

      // Get pricing from metadata
      const discountedPrice = productView.metadata?.discountedPrice;
      const originalPrice = productView.metadata?.price;
      const displayPrice = discountedPrice || originalPrice || 0;

      return {
        productId: productView.productId,
        productUrl: productView.productUrl,
        subject,
        productName,
        displayPrice,
        originalPrice,
        checkoutUrl: productView.checkoutUrl,
      };
    });
  }, [suggestedProducts]);

  return {
    suggestedProducts,
    isLoading,
    error,
    cardProps,
  };
}
